import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import MultiSelect from "../applicationUi/components/MultiSelect";
import { fetchUsersWithCalendars } from "../store/thunks/cliniciansThunk";
import {
  checkMeetingConflicts,
  createMeeting,
  updateMeetingThunk,
  deleteMeetingThunk,
  checkMeetingResourceAvailability,
  previewRecurringMeetings,
} from "../store/thunks/meetingsThunk";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from 'react-toastify';
import {
  XMarkIcon,
  CalendarIcon,
  ClockIcon,
  UserIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { Dialog, Transition } from "@headlessui/react";

// Helper function to round time to nearest 5 minutes
const roundTimeToNearest5Minutes = (time) => {
  const [hours, minutes] = time.split(":").map(Number);
  const roundedMinutes = Math.round(minutes / 5) * 5;
  return `${hours.toString().padStart(2, "0")}:${roundedMinutes
    .toString()
    .padStart(2, "0")}`;
};

// Generate time options in 5-minute intervals
const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 5) {
      const timeString = `${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;
      const displayTime = new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      options.push({ value: timeString, label: displayTime });
    }
  }
  return options;
};

const QuickAddMeeting = ({
  isOpen,
  onClose,
  // For new meetings
  initialDate,
  initialTime,
  selectedClinician,
  selectedClinic,
  // For existing meetings
  selectedMeeting,
}) => {
  const dispatch = useDispatch();
  const clinicians = useSelector((state) => state.clinicians.clinicians);
  const user = useSelector((state) => state.auth.user);
  const resources = useSelector((state) => state.resources?.resources || []);
  const isUpdating = Boolean(selectedMeeting);
  const timeOptions = generateTimeOptions();

  console.log(
    "[QUICKADDMEETING] - This is the selected meeting:",
    selectedMeeting
  );

  // Initialize state based on whether we're creating or updating
  const [selectedDate, setSelectedDate] = useState(
    isUpdating
      ? new Date(selectedMeeting.date)
      : initialDate
      ? new Date(initialDate)
      : new Date()
  );

  const [selectedClinicians, setSelectedClinicians] = useState(
    isUpdating
      ? selectedMeeting.attendees.map((attendee) => ({
          value: attendee._id,
          label: `${attendee.firstName} ${attendee.lastName}`,
        }))
      : selectedClinician
      ? [
          {
            value: selectedClinician._id,
            label: `${selectedClinician.firstName} ${selectedClinician.lastName}`,
          },
        ]
      : []
  );

  const [meetingTitle, setMeetingTitle] = useState(
    isUpdating ? selectedMeeting.title : ""
  );
  const [meetingDescription, setMeetingDescription] = useState(
    isUpdating ? selectedMeeting.description : ""
  );
  const [meetingDuration, setMeetingDuration] = useState(
    isUpdating ? selectedMeeting.duration : 60
  );
  const [meetingTime, setMeetingTime] = useState(
    isUpdating
      ? selectedMeeting.startTime
      : initialTime
      ? roundTimeToNearest5Minutes(initialTime)
      : ""
  );
  const [resourceAvailability, setResourceAvailability] = useState({});
  const [selectedResources, setSelectedResources] = useState(
    isUpdating && selectedMeeting.resources ? selectedMeeting.resources : []
  );
  const [resourceTypeFilter, setResourceTypeFilter] = useState("");

  // Recurring meeting state
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState("weekly");
  const [recurringInterval, setRecurringInterval] = useState(1);
  const [recurringDaysOfWeek, setRecurringDaysOfWeek] = useState([]);
  const [recurringDayOfMonth, setRecurringDayOfMonth] = useState(1);
  const [recurringEndType, setRecurringEndType] = useState("date"); // "date" or "occurrences"
  const [recurringEndDate, setRecurringEndDate] = useState(
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days from now
  );
  const [recurringMaxOccurrences, setRecurringMaxOccurrences] = useState(10);
  const [showRecurringPreview, setShowRecurringPreview] = useState(false);

  // Get recurring preview from Redux state
  const recurringPreview = useSelector((state) => state.meetings.recurringPreview);
  const recurringPreviewLoading = useSelector((state) => state.meetings.recurringPreviewLoading);

  useEffect(() => {
    // Fetch users with calendars instead of just clinicians
    // This includes admins, owners, and anyone else who has a calendar set up
    dispatch(fetchUsersWithCalendars());
  }, [dispatch]);

  // Fetch available resources when date, time, or duration changes
  useEffect(() => {
    if (selectedDate && meetingTime && meetingDuration && selectedClinic) {
      const [hours, minutes] = meetingTime.split(":").map(Number);
      const start = new Date(selectedDate);
      start.setHours(hours);
      start.setMinutes(minutes);

      dispatch(
        checkMeetingResourceAvailability({
          clinicId: selectedClinic._id,
          start: start.toISOString(),
          duration: meetingDuration,
        })
      ).then((action) => {
        // Store the availability response in a more easily accessible format
        const availabilityMap = {};
        action.payload.forEach((resource) => {
          availabilityMap[resource.resourceId] = {
            bookingStatus: resource.bookingStatus,
            overlappingBookings: resource.overlappingBookings,
          };
        });
        setResourceAvailability(availabilityMap);
      });
    }
  }, [selectedDate, meetingTime, meetingDuration, selectedClinic, dispatch]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleClinicianChange = (selectedOptions) => {
    setSelectedClinicians(selectedOptions);
  };

  const handleTimeChange = (e) => {
    setMeetingTime(e.target.value);
  };

  const handleRemoveClinician = (clinicianId) => {
    setSelectedClinicians((prev) =>
      prev.filter((c) => c.value !== clinicianId)
    );
  };

  const handleResourceSelection = (resource) => {
    const availability = resourceAvailability[resource._id];
    if (!availability || availability.bookingStatus !== "Available") return;

    // Check if already selected
    const isAlreadySelected = selectedResources.some(
      (r) => r._id === resource._id
    );

    if (isAlreadySelected) {
      // Remove from selected resources
      setSelectedResources((prev) =>
        prev.filter((r) => r._id !== resource._id)
      );
    } else {
      // Add to selected resources
      setSelectedResources((prev) => [...prev, resource]);
    }
  };

  const handleBookAllDay = (event) => {
    event.preventDefault(); // Prevent unintended form submissions
    setMeetingTime("08:00"); // Set start time to 08:00
    setMeetingDuration(720); // Set duration to 12 hours
  };

  // Handle toggling recurring checkbox
  const handleRecurringToggle = (checked) => {
    setIsRecurring(checked);
    if (!checked) {
      setShowRecurringPreview(false);
    }
  };

  // Handle day of week selection for weekly recurring
  const handleDayOfWeekToggle = (dayIndex) => {
    setRecurringDaysOfWeek(prev => {
      if (prev.includes(dayIndex)) {
        return prev.filter(d => d !== dayIndex);
      } else {
        return [...prev, dayIndex].sort();
      }
    });
  };

  // Generate preview of recurring meetings
  const handlePreviewRecurring = async () => {
    if (!selectedDate || !meetingTime) {
      toast.warning("Please select a date and time first");
      return;
    }

    const recurringPattern = {
      frequency: recurringFrequency,
      interval: recurringInterval,
      ...(recurringFrequency === "weekly" && recurringDaysOfWeek.length > 0 && {
        daysOfWeek: recurringDaysOfWeek
      }),
      ...(recurringFrequency === "monthly" && {
        dayOfMonth: recurringDayOfMonth
      }),
      ...(recurringEndType === "date" && {
        endDate: recurringEndDate
      }),
      ...(recurringEndType === "occurrences" && {
        maxOccurrences: recurringMaxOccurrences
      })
    };

    try {
      await dispatch(previewRecurringMeetings({
        recurringPattern,
        startDate: selectedDate
      }));
      setShowRecurringPreview(true);
    } catch (error) {
      console.error("Failed to preview recurring meetings:", error);
      toast.error("Failed to preview recurring meetings");
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();

    const clinicId = isUpdating ? selectedMeeting.clinic : selectedClinic?._id;

    if (!clinicId) {
      console.error("Clinic is not selected or invalid.");
      toast.warning("Please select a clinic before saving the meeting.");
      return;
    }

    const meetingData = {
      clinic: clinicId,
      title: meetingTitle,
      date: selectedDate,
      startTime: meetingTime,
      duration: meetingDuration,
      attendees: selectedClinicians.map((clinician) => clinician.value),
      resources: selectedResources.map((resource) => resource._id),
      description: meetingDescription,
      createdBy: user._id,
    };

    // Add recurring pattern if this is a recurring meeting
    if (isRecurring && !isUpdating) {
      meetingData.isRecurring = true;
      meetingData.recurringPattern = {
        frequency: recurringFrequency,
        interval: recurringInterval,
        ...(recurringFrequency === "weekly" && recurringDaysOfWeek.length > 0 && {
          daysOfWeek: recurringDaysOfWeek
        }),
        ...(recurringFrequency === "monthly" && {
          dayOfMonth: recurringDayOfMonth
        }),
        ...(recurringEndType === "date" && {
          endDate: recurringEndDate
        }),
        ...(recurringEndType === "occurrences" && {
          maxOccurrences: recurringMaxOccurrences
        })
      };
    }

    const conflictCheckResult = await dispatch(
      checkMeetingConflicts(meetingData)
    );

    if (conflictCheckResult.payload?.conflicts?.length > 0) {
      if (
        !window.confirm(
          `${conflictCheckResult.payload.message}. Do you want to proceed?`
        )
      ) {
        return;
      }
    }

    try {
      if (isUpdating) {
        await dispatch(
          updateMeetingThunk({
            id: selectedMeeting._id,
            updatedData: meetingData,
          })
        );
        toast.success("Meeting updated successfully");
      } else {
        const result = await dispatch(createMeeting(meetingData));
        if (result.payload?.isRecurring) {
          toast.success(`Successfully created ${result.payload.totalCreated} recurring meetings`);
        } else {
          toast.success("Meeting created successfully");
        }
      }
      onClose();
    } catch (error) {
      console.error(
        `Failed to ${isUpdating ? "update" : "create"} meeting:`,
        error
      );
      toast.error(`Failed to ${isUpdating ? "update" : "create"} meeting`);
    }
  };

  const handleDelete = async () => {
    if (!isUpdating) return;

    if (window.confirm("Are you sure you want to delete this meeting?")) {
      try {
        await dispatch(deleteMeetingThunk(selectedMeeting._id));
        onClose();
      } catch (error) {
        console.error("Failed to delete meeting:", error);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-sm bg-white p-6 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 pb-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-indigo-100 rounded-sm flex items-center justify-center">
                        <CalendarIcon className="w-5 h-5 text-indigo-600" />
                      </div>
                    </div>
                    <div>
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold text-gray-900">
                        {isUpdating ? "Edit Meeting" : "Schedule New Meeting"}
                      </Dialog.Title>
                      <p className="text-sm text-gray-500">
                        {isUpdating
                          ? "Update meeting details and resources"
                          : "Create a new meeting and reserve resources"}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="rounded-sm bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    onClick={onClose}>
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleSave} className="space-y-6">
                  {/* Meeting Details Section */}
                  <div className="bg-gray-50 rounded-sm p-4 space-y-4">
                    <div className="flex items-center space-x-2">
                      <DocumentTextIcon className="h-5 w-5 text-gray-500" />
                      <h3 className="text-base font-medium text-gray-900">
                        Meeting Details
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {/* Meeting Title */}
                      <div className="sm:col-span-2">
                        <label
                          htmlFor="meeting-title"
                          className="block text-sm font-medium text-gray-700 mb-1">
                          Meeting Title <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="meeting-title"
                          id="meeting-title"
                          required
                          value={meetingTitle}
                          onChange={(e) => setMeetingTitle(e.target.value)}
                          className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          placeholder="Enter meeting title..."
                        />
                      </div>

                      {/* Description */}
                      <div className="sm:col-span-2">
                        <label
                          htmlFor="meeting-description"
                          className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          name="meeting-description"
                          id="meeting-description"
                          value={meetingDescription}
                          onChange={(e) =>
                            setMeetingDescription(e.target.value)
                          }
                          rows="3"
                          className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          placeholder="Enter meeting details..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Attendees Section */}
                  <div className="bg-blue-50 rounded-sm p-4 space-y-4">
                    <div className="flex items-center space-x-2">
                      <UserIcon className="h-5 w-5 text-blue-600" />
                      <h3 className="text-base font-medium text-gray-900">
                        Attendees
                      </h3>
                    </div>

                    {/* Selected Attendees Display */}
                    {selectedClinicians.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {selectedClinicians.map((clinician) => (
                          <span
                            key={clinician.value}
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                            {clinician.label}
                            <button
                              type="button"
                              className="ml-2 text-indigo-600 hover:text-indigo-800"
                              onClick={() =>
                                handleRemoveClinician(clinician.value)
                              }>
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    <div>
                      <MultiSelect
                        options={clinicians.map((clinician) => ({
                          value: clinician._id,
                          label: `${clinician.firstName} ${clinician.lastName}`,
                        }))}
                        selectedValues={selectedClinicians}
                        onChange={handleClinicianChange}
                        placeholder="Select meeting attendees..."
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Date & Time Section */}
                  <div className="bg-green-50 rounded-sm p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <CalendarIcon className="h-5 w-5 text-green-600" />
                        <h3 className="text-base font-medium text-gray-900">
                          Schedule
                        </h3>
                      </div>
                      <button
                        type="button"
                        className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-700 bg-blue-100 rounded-sm hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onClick={handleBookAllDay}
                        disabled={!selectedDate}>
                        <ClockIcon className="h-4 w-4 mr-1" />
                        All Day
                      </button>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      {/* Date */}
                      <div className="flex-1">
                        <label
                          htmlFor="meeting-date"
                          className="block text-sm font-medium text-gray-700 mb-1">
                          Date <span className="text-red-500">*</span>
                        </label>
                        <DatePicker
                          selected={selectedDate}
                          onChange={handleDateChange}
                          dateFormat="MMMM d, yyyy"
                          className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          required
                        />
                      </div>

                      {/* Start Time */}
                      <div className="flex-1">
                        <label
                          htmlFor="meeting-time"
                          className="block text-sm font-medium text-gray-700 mb-1">
                          Start Time <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="meeting-time"
                          id="meeting-time"
                          required
                          value={meetingTime}
                          onChange={handleTimeChange}
                          className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                          <option value="">Select time...</option>
                          {timeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Duration */}
                      <div className="flex-1">
                        <label
                          htmlFor="meeting-duration"
                          className="block text-sm font-medium text-gray-700 mb-1">
                          Duration (mins){" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="meeting-duration"
                          id="meeting-duration"
                          required
                          value={meetingDuration}
                          onChange={(e) =>
                            setMeetingDuration(
                              Math.max(5, Math.round(e.target.value / 5) * 5)
                            )
                          }
                          className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          step="5"
                          min="5"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Resources Section */}
                  <div className="bg-yellow-50 rounded-sm p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <BuildingOfficeIcon className="h-5 w-5 text-yellow-600" />
                        <h3 className="text-base font-medium text-gray-900">
                          Resource Booking
                        </h3>
                      </div>
                      {selectedResources.length > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {selectedResources.length} Selected
                        </span>
                      )}
                    </div>

                    {selectedDate && meetingTime && meetingDuration ? (
                      <>
                        {/* Resource Type Filter */}
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-gray-600">
                            Select resources for your meeting
                          </p>
                          <select
                            value={resourceTypeFilter}
                            onChange={(e) =>
                              setResourceTypeFilter(e.target.value)
                            }
                            className="rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                            <option value="">All Types</option>
                            {Array.from(
                              new Set(resources.map((r) => r.type))
                            ).map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Resource Grid */}
                        <div className="space-y-4">
                          {Object.entries(
                            resources
                              .filter((resource) =>
                                resourceTypeFilter
                                  ? resource.type === resourceTypeFilter
                                  : true
                              )
                              .reduce((acc, resource) => {
                                if (!acc[resource.type])
                                  acc[resource.type] = [];
                                acc[resource.type].push(resource);
                                return acc;
                              }, {})
                          ).length > 0 ? (
                            Object.entries(
                              resources
                                .filter((resource) =>
                                  resourceTypeFilter
                                    ? resource.type === resourceTypeFilter
                                    : true
                                )
                                .reduce((acc, resource) => {
                                  if (!acc[resource.type])
                                    acc[resource.type] = [];
                                  acc[resource.type].push(resource);
                                  return acc;
                                }, {})
                            ).map(([type, typeResources]) => (
                              <div
                                key={type}
                                className="bg-white rounded-sm border border-gray-200 p-4">
                                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                                  <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></span>
                                  {type}
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {typeResources.map((resource) => {
                                    const availability =
                                      resourceAvailability[resource._id];
                                    const isSelected = selectedResources.some(
                                      (r) => r._id === resource._id
                                    );
                                    const isAvailable =
                                      availability?.bookingStatus ===
                                      "Available";

                                    return (
                                      <div
                                        key={resource._id}
                                        className={`relative flex items-start p-3 rounded-sm border-2 transition-colors ${
                                          isSelected
                                            ? "border-indigo-500 bg-indigo-50"
                                            : isAvailable
                                            ? "border-gray-200 hover:border-gray-300"
                                            : "border-gray-200 bg-gray-50"
                                        }`}>
                                        <div className="flex items-center h-5">
                                          <input
                                            id={`resource-${resource._id}`}
                                            name={`resource-${resource._id}`}
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() =>
                                              handleResourceSelection(resource)
                                            }
                                            disabled={!isAvailable}
                                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50"
                                          />
                                        </div>
                                        <div className="ml-3 text-sm flex-1">
                                          <label
                                            htmlFor={`resource-${resource._id}`}
                                            className={`font-medium ${
                                              isAvailable
                                                ? "text-gray-900"
                                                : "text-gray-500"
                                            }`}>
                                            {resource.name}
                                          </label>
                                          {resource.description && (
                                            <p className="text-gray-500 text-xs mt-1">
                                              {resource.description}
                                            </p>
                                          )}
                                          {resource.location && (
                                            <p className="text-gray-400 text-xs">
                                              {resource.location}
                                            </p>
                                          )}
                                          <div className="flex items-center mt-2">
                                            {isAvailable ? (
                                              <div className="flex items-center text-green-600">
                                                <CheckCircleIcon className="h-4 w-4 mr-1" />
                                                <span className="text-xs font-medium">
                                                  Available
                                                </span>
                                              </div>
                                            ) : (
                                              <div className="flex items-center text-orange-600">
                                                <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                                                <span className="text-xs font-medium">
                                                  {availability?.bookingStatus ||
                                                    "Checking..."}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
                              <h3 className="mt-2 text-sm font-medium text-gray-900">
                                No resources available
                              </h3>
                              <p className="mt-1 text-sm text-gray-500">
                                No resources found for the selected filter.
                              </p>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">
                          Schedule Meeting First
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Please select a date, time, and duration to view
                          available resources.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Recurring Meeting Section */}
                  {!isUpdating && (
                    <div className="bg-purple-50 rounded-sm p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <CalendarIcon className="h-5 w-5 text-purple-600" />
                          <h3 className="text-base font-medium text-gray-900">
                            Recurring Meeting
                          </h3>
                        </div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={isRecurring}
                            onChange={(e) => handleRecurringToggle(e.target.checked)}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            Make this a recurring meeting
                          </span>
                        </label>
                      </div>

                      {isRecurring && (
                        <div className="space-y-4">
                          {/* Frequency Selection */}
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Frequency
                              </label>
                              <select
                                value={recurringFrequency}
                                onChange={(e) => setRecurringFrequency(e.target.value)}
                                className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm">
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Every
                              </label>
                              <input
                                type="number"
                                min="1"
                                max="10"
                                value={recurringInterval}
                                onChange={(e) => setRecurringInterval(Math.max(1, parseInt(e.target.value) || 1))}
                                className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                              />
                            </div>

                            <div className="flex items-end">
                              <span className="text-sm text-gray-600 pb-2">
                                {recurringFrequency === "daily" && `day${recurringInterval > 1 ? 's' : ''}`}
                                {recurringFrequency === "weekly" && `week${recurringInterval > 1 ? 's' : ''}`}
                                {recurringFrequency === "monthly" && `month${recurringInterval > 1 ? 's' : ''}`}
                              </span>
                            </div>
                          </div>

                          {/* Weekly specific options */}
                          {recurringFrequency === "weekly" && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                On these days
                              </label>
                              <div className="flex space-x-2">
                                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day, index) => (
                                  <button
                                    key={day}
                                    type="button"
                                    onClick={() => handleDayOfWeekToggle(index)}
                                    className={`px-3 py-1 text-sm rounded-sm border ${
                                      recurringDaysOfWeek.includes(index)
                                        ? "bg-purple-100 border-purple-500 text-purple-700"
                                        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                                    }`}>
                                    {day}
                                  </button>
                                ))}
                              </div>
                              {recurringDaysOfWeek.length === 0 && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Leave empty to use the same day of the week as the start date
                                </p>
                              )}
                            </div>
                          )}

                          {/* Monthly specific options */}
                          {recurringFrequency === "monthly" && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Day of month
                              </label>
                              <input
                                type="number"
                                min="1"
                                max="31"
                                value={recurringDayOfMonth}
                                onChange={(e) => setRecurringDayOfMonth(Math.max(1, Math.min(31, parseInt(e.target.value) || 1)))}
                                className="block w-20 rounded-sm border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Leave as 1 to use the same date as the start date
                              </p>
                            </div>
                          )}

                          {/* End options */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                End
                              </label>
                              <select
                                value={recurringEndType}
                                onChange={(e) => setRecurringEndType(e.target.value)}
                                className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm">
                                <option value="date">On date</option>
                                <option value="occurrences">After occurrences</option>
                              </select>
                            </div>

                            <div>
                              {recurringEndType === "date" ? (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    End date
                                  </label>
                                  <DatePicker
                                    selected={recurringEndDate}
                                    onChange={setRecurringEndDate}
                                    dateFormat="MMM d, yyyy"
                                    minDate={selectedDate}
                                    className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                  />
                                </div>
                              ) : (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Occurrences
                                  </label>
                                  <input
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={recurringMaxOccurrences}
                                    onChange={(e) => setRecurringMaxOccurrences(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                                    className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                  />
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Preview Button */}
                          <div className="flex justify-between items-center">
                            <button
                              type="button"
                              onClick={handlePreviewRecurring}
                              disabled={!selectedDate || !meetingTime || recurringPreviewLoading}
                              className="inline-flex items-center px-3 py-1 text-sm font-medium text-purple-700 bg-purple-100 rounded-sm hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50">
                              <CalendarIcon className="h-4 w-4 mr-1" />
                              {recurringPreviewLoading ? "Loading..." : "Preview Dates"}
                            </button>
                          </div>

                          {/* Preview Results */}
                          {showRecurringPreview && recurringPreview && (
                            <div className="bg-white rounded-sm border border-purple-200 p-3">
                              <h4 className="text-sm font-medium text-gray-900 mb-2">
                                Preview: {recurringPreview.count} meetings will be created
                              </h4>
                              <div className="max-h-32 overflow-y-auto">
                                <div className="text-xs text-gray-600 space-y-1">
                                  {recurringPreview.dates.slice(0, 10).map((date, index) => (
                                    <div key={index}>
                                      {new Date(date).toLocaleDateString()} at {meetingTime}
                                    </div>
                                  ))}
                                  {recurringPreview.count > 10 && (
                                    <div className="text-gray-500">
                                      ... and {recurringPreview.count - 10} more
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                    {isUpdating && (
                      <button
                        type="button"
                        onClick={handleDelete}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-sm hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500">
                        <TrashIcon className="h-4 w-4 mr-2" />
                        Delete Meeting
                      </button>
                    )}
                    <div
                      className={`flex items-center space-x-3 ${
                        !isUpdating ? "ml-auto" : ""
                      }`}>
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                        {isUpdating ? (
                          <>
                            <CheckCircleIcon className="h-4 w-4 mr-2" />
                            Update Meeting
                          </>
                        ) : (
                          <>
                            <CalendarIcon className="h-4 w-4 mr-2" />
                            Create Meeting
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default QuickAddMeeting;
